/**
 * Deterministic, per-run option shuffling.
 *
 * Options are presented in a different order on every run (anti-memorisation — you can't learn "the
 * answer is C"), but the order is STABLE within a run: it's derived from a per-run `seed` (stored in
 * the game machine's context, so it survives autosave/resume) plus a per-scenario salt. Scoring always
 * keys off the stable option id, never its display position.
 */

/** A deterministically shuffled copy of `items` for (seed, salt). Same inputs → same order. */
export function seededOrder<T>(items: readonly T[], seed: number, salt: string): T[] {
  // Fold the salt into the seed (FNV-1a-style), then drive a mulberry32 PRNG.
  let h = seed >>> 0;
  for (let i = 0; i < salt.length; i++) {
    h = Math.imul(h ^ salt.charCodeAt(i), 0x01000193) >>> 0;
  }
  const rng = () => {
    h = (h + 0x6d2b79f5) | 0;
    let t = Math.imul(h ^ (h >>> 15), 1 | h);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
